import { type AppSlug } from '@shared/all/domains/apps';
import {
  DEFAULT_NON_PREMIUM_MARGIN_PERCENT,
  type Product
} from '@shared/all/domains/products';
import { useQuery } from '@tanstack/react-query';

import { unwrapFromResult } from '../helpers/resultWrapper';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

type Platform = 'web' | 'ios' | 'android';

// The catalog rarely changes and a product screen mounts several readers at
// once, so React Query dedupes them and keeps the result warm across
// navigations. Replaces the old ProductContext + app-boot fetch: pageviews
// that never show a price now make no products query at all.
const STALE_TIME_MS = 5 * 60 * 1000;

const EMPTY_PRODUCTS: Product[] = [];

// Pricing facts every storefront needs alongside the catalog. `marginPercent`
// is the SPEND-side rate: trons sell flat, and a non-premium member burns this
// much extra on every call. Returned here (not re-derived per screen) so web
// and mobile can never disagree about what a balance is worth.
// `app` is which storefront is asking, and it is not the platform: Factiii and
// Oakbox both run on ios, and each sells its own catalogue. It is part of the
// query key for the same reason — one app's list must never be served from the
// other's cache entry.
const useProducts = (
  client: WrappedTRPCClient,
  platform: Platform = 'web',
  app: AppSlug = 'factiii'
) => {
  const { data, isPending } = useQuery({
    queryKey: ['products', platform, app],
    queryFn: async () => {
      const result = await unwrapFromResult(
        client.products.getPurchasableProducts.query({ platform, app })
      );
      return result;
    },
    staleTime: STALE_TIME_MS
  });

  return {
    // True until the first answer (or failure) lands. `products` is empty the
    // whole time, so a caller deciding "there is nothing to sell" has to wait
    // for this to go false first.
    isPending,
    products: data?.products ?? EMPTY_PRODUCTS,
    isPremium: data?.isPremium ?? false,
    marginPercent: data?.marginPercent ?? DEFAULT_NON_PREMIUM_MARGIN_PERCENT,
    promoBoostPercent: data?.promoBoostPercent ?? 0
  };
};

export default useProducts;
